import React, { useEffect } from 'react';
import styles from 'styles/components/question-builders/QuestionBuildersList.module.css';
import { ExpandableQuestion } from 'components/expandable/ExpandableQuestion';
import { getScreenerPreview } from 'helpers/questionPreviewBuilder';
import { getQuestion } from 'helpers/questionBuilder';
import {
  addUnique,
  findNext,
  fixOrder,
  removeById,
  removeByValue,
} from '@ax2/common/helpers/array';
import _ from 'lodash';
import { ActionButton } from '@ax2/common/components/ActionButton';
import { QuestionListHeader } from 'components/QuestionListHeader';
import { getQuestionsValidation } from 'helpers/questionBuilderValidations';
import UUID from '@ax2/common/model/UUID';
import {
  useSurveyContext,
  useSurveyDispatchContext,
} from '../../contexts/SurveyContext';
import { storeSurvey } from 'rest/makeSurveyClient';
import nextIcon from '../../assets/arrow_right_white.svg';
import plusIcon from '../../assets/plus_blue.svg';
import { ScreenersLibraryModal } from 'components/ScreenersLibraryModal';
import {
  Element as ScrollElement,
  Events,
  scroller,
} from 'react-scroll/modules';
import { isScreenersValid } from 'helpers/survey';
import { isEditable } from 'helpers/question';
import ScreenerQuestionSchema from '@ax2/common/model/schemas/ScreenerQuestionSchema';
import QuestionSchema from '@ax2/common/model/schemas/QuestionSchema';
import QuestionType from '@ax2/common/model/QuestionType';
import config from '../../config';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from '@ax2/common/rest/baseClient';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';

export interface ScreenerBuildersListData {
  onFinish: () => void;
  colorFunction: () => void;
  setcolor3false: () => void;
}
export const ScreenerBuildersList: React.FC<
  ScreenerBuildersListData
> = props => {
  const surveySchema = useSurveyContext();
  const surveyDispatch = useSurveyDispatchContext();
  const updateError = useErrorUpdateContext();

  useEffect(() => {
    if (surveySchema && !surveySchema.title && config.type === 'demo') {
      surveyDispatch({ type: 'title', title: 'Demo Survey' }); //Prefill title in fast Demo mode
    }
  }, [surveyDispatch, surveySchema]);

  const [expandedQuestions, setExpandedQuestions] = React.useState<UUID[]>([]);
  const [isScreenerLibVisible, setIsScreenerLibVisible] = React.useState(false);
  const [isErrorsVisible, setIsErrorsVisible] = React.useState<boolean>(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const errors = surveySchema
    ? getQuestionsValidation(surveySchema.screeners)
    : [];
  const isSurveyScreenersValid = isScreenersValid(surveySchema);
  const updateSurveyWithScreeners = (screeners: ScreenerQuestionSchema[]) => {
    if (surveySchema) {
      surveyDispatch({
        type: 'screeners',
        screeners: fixOrder(screeners),
      });
    }
  };
  const updateStoreWithScreeners = async (
    screeners: ScreenerQuestionSchema[]
  ) => {
    try {
      updateSurveyWithScreeners(screeners);
      if (surveySchema) {
        await storeSurvey(
          {
            ...surveySchema,
            ...{
              screeners: fixOrder(screeners),
            },
          },
          false
        );
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };

  const changeScreener = async (
    newQuestion: QuestionSchema,
    position: number
  ) => {
    if (surveySchema && newQuestion.questionType !== QuestionType.Empty) {
      const screeners = [...surveySchema.screeners];
      const oldScreener = screeners[position];
      const newScreener: ScreenerQuestionSchema = {
        ...oldScreener,
        ...newQuestion,
      };
      screeners[position] = newScreener;
      props.setcolor3false();
      updateSurveyWithScreeners(screeners);
    }
  };
  const removeQuestion = async (id: UUID) => {
    try {
      if (surveySchema) {
        setExpandedQuestions(removeByValue(expandedQuestions, id));
        await updateStoreWithScreeners(removeById(surveySchema.screeners, id));
      }
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const replaceQuestion = async (id: UUID) => {
    try {
      await removeQuestion(id);
      handleNewScreener();
    } catch (e) {
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };

  const saveAsDraftHandler = async () => {
    try {
      setIsErrorsVisible(true);
      if (surveySchema) {
        setIsSaving(true);
        await storeSurvey(surveySchema, false);
        setIsSaving(false);
      }
    } catch (e) {
      setIsSaving(false);
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const save = async () => {
    try {
      setIsErrorsVisible(true);
      if (surveySchema) {
        setIsSaving(true);
        await storeSurvey(surveySchema, false);
        setIsSaving(false);
        props.colorFunction();
        props.onFinish();
      }
    } catch (e) {
      setIsSaving(false);
      updateError({
        text: getErrorMessage(e as RestError),
      });
    }
  };
  const handleNewScreener = () => {
    setIsScreenerLibVisible(true);
    props.setcolor3false();
  };
  return (
    <>
      {isScreenerLibVisible && (
        <ScreenersLibraryModal
          onClose={() => {
            setIsScreenerLibVisible(false);
            if (surveySchema) {
              updateStoreWithScreeners(surveySchema.screeners);
            }
          }}
        />
      )}
      {getActions(
        isErrorsVisible && !isSurveyScreenersValid,
        saveAsDraftHandler,
        save,
        isSaving
      )}
      <QuestionListHeader
        title="Step 1"
        subtitle="Screener questions will help sort out candidates that don’t fit specific criteria you may like."
        notes={[
          'You may select up to 2 screener questions from our pre-made options.',
          'These questions are either semi-editable or non-editable.',
        ]}
        helperText="If you do not want screener questions then just proceed to Survey Questions by clicking the red Save & Continue to Survey Questions button."
        rightElement={
          <ActionButton
            text="Add a screener question"
            type="secondary"
            icon={{
              source: plusIcon,
              size: 'xnormal',
              position: 'top',
            }}
            onClick={handleNewScreener}
          />
        }
      />
      {surveySchema &&
        _.map(surveySchema.screeners, (screener, index) => (
          <div className={styles.questionWrapper} key={index}>
            <ScrollElement name={`scrollScreener_${screener.id}`}>
              <ExpandableQuestion
                id={'sbl-question-' + (index + 1)}
                title={'Screener ' + (index + 1)}
                subtitle={isEditable(screener) ? 'Editable' : 'Non-Editable'}
                isScreener={true}
                state={
                  isErrorsVisible
                    ? errors[index]
                      ? 'needsEdit'
                      : 'edited'
                    : undefined
                }
                removeCompletion={async () => removeQuestion(screener.id)}
                replaceCompletion={async () => {
                  replaceQuestion(screener.id);
                }}
                swapCompletion={async ql => {
                  updateStoreWithScreeners(ql as ScreenerQuestionSchema[]);
                }}
                previewContent={getScreenerPreview(screener)}
                expandedContent={getQuestion(
                  screener,
                  true,
                  (q: QuestionSchema) => changeScreener(q, index),
                  {
                    saveCompletion: async () => {
                      try {
                        await storeSurvey(surveySchema, false);
                        const nextQuestion = findNext(
                          surveySchema.screeners,
                          q => q.id === screener.id
                        );

                        if (nextQuestion) {
                          const updatedExpandedQuestions = removeByValue(
                            expandedQuestions,
                            screener.id
                          );
                          setTimeout(() => {
                            new Promise(resolve => {
                              Events.scrollEvent.register('end', () => {
                                resolve();
                                Events.scrollEvent.remove('end');
                              });
                              scroller.scrollTo(
                                'scrollScreener_' + nextQuestion.id,
                                {
                                  duration: 400,
                                  delay: 0,
                                  offset: -20,
                                  smooth: 'easeInOutQuart',
                                }
                              );
                            }).then(() => {
                              // TODO: remove then and use async
                              setExpandedQuestions(
                                addUnique(
                                  updatedExpandedQuestions,
                                  nextQuestion.id
                                )
                              );
                            });
                          }, 1200); // (hide content) + (show preview) = time to 'collapse' current question

                          setExpandedQuestions(updatedExpandedQuestions);
                        } else {
                          setExpandedQuestions(
                            removeByValue(expandedQuestions, screener.id)
                          );
                        }
                      } catch (e) {
                        updateError({
                          text: getErrorMessage(e as RestError),
                        });
                      }
                    },
                  }
                )}
                isExpanded={expandedQuestions.includes(screener.id)}
                onExpand={state => {
                  setExpandedQuestions(
                    state
                      ? addUnique(expandedQuestions, screener.id)
                      : removeByValue(expandedQuestions, screener.id)
                  );
                }}
              />
            </ScrollElement>
          </div>
        ))}
      {getActions(false, saveAsDraftHandler, save, isSaving)}
    </>
  );
};

const getActions = (
  hasErrors: boolean,
  onSaveAsDraft: () => void,
  onSaveAndSend: () => void,
  isSaving: boolean
) => {
  return (
    <div className={styles.actionsContent}>
      <div className={styles.actionsWithError}>
        <ActionButton
          text="Save as draft"
          type="secondaryAlt"
          onClick={() => !isSaving && onSaveAsDraft()}
        />
        {hasErrors && (
          <div className={styles.error}>
            <i className="material-icons">error_outline</i>
            <span>
              There are some screener questions below that need to be reviewed
              before processing
            </span>
          </div>
        )}
      </div>
      <ActionButton
        text="Save and continue to survey questions"
        type="primaryAlt"
        icon={{
          source: nextIcon,
          position: 'right',
        }}
        onClick={() => !isSaving && onSaveAndSend()}
      />
    </div>
  );
};
